import React, { FC, useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { Formik } from 'formik'
import OtpInput from 'react-otp-input'
import { IFormProps, constants } from 'utils'
import {
  Module,
  ModuleBody,
  ModuleHeader,
  ButtonContainer,
  CustomAlert,
  CustomButton,
} from 'components'
import useStyles from '../../mfa-style'
import { api, routes } from 'config'
import axios from 'axios'
import { formatPhoneNumber } from 'react-phone-number-input'
import { useNavigate, useLocation } from 'react-router-dom'

let executeOnce = false
interface IAdditionalProps extends IFormProps {
  handleResend: (event: any) => void
  transaction: any
  count: number
  setShowLoader?: any
  phoneNumber?: string
  onUseDifferentMethod?: () => void
}

const MfaOtpForm: FC<IAdditionalProps> = ({
  initialValues,
  validationSchema,
  handleSubmit,
  serviceError,
  transaction,
  count,
  setShowLoader,
  phoneNumber,
  onUseDifferentMethod,
}) => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileNumber, setMobileNumber] = useState<string | null>()
  const [currentState, setCurrentState] = useState<string | null>()
  const [disableResend, setDisableResend] = useState(false)
  const [tooManyAttemptsAlert, setTooManyAttemptsAlert] = useState(false)
  const [resendAlert, setResendAlert] = useState(false)
  const [requestFailed, setRequestFailed] = useState(false)
  const [clearOTP, setClearOTP] = useState(false)
  const [alertVisibility, setAlertVisibility] = useState(true)
  const [factorType, setFactorType] = useState<'sms' | 'email' | 'totp'>('sms')

  useEffect(() => {
    setDisableResend(true)
    setTimeout(() => {
      setDisableResend(false)
    }, 30000)
    getmobileNumber(transaction)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getmobileNumber = async (response: any) => {
    // Detect factor type from the transaction
    const currentFactorType = response.factor?.factorType
    if (currentFactorType === 'email') {
      setFactorType('email')
    } else if (
      currentFactorType === 'token:software:totp' ||
      currentFactorType === 'totp'
    ) {
      setFactorType('totp')
    } else {
      setFactorType('sms')
    }

    if (location.pathname === routes.updateEnrollmentOtpForm) {
      setCurrentState('API_SUCCESS')
      setMobileNumber(phoneNumber)
    } else if (
      response.status === constants.OKTA_TRANSACTION_STATUS.MFA_CHALLENGE
    ) {
      // For email and TOTP factors, we don't need to fetch phone number
      if (currentFactorType === 'email') {
        const email =
          response.factor?.profile?.email || response.user?.profile?.login
        setMobileNumber(email) // Reuse mobileNumber state for email display
        setCurrentState('API_SUCCESS')
        setShowLoader(false)
        return
      }
      if (
        currentFactorType === 'token:software:totp' ||
        currentFactorType === 'totp'
      ) {
        setMobileNumber('authenticator') // Placeholder to trigger render
        setCurrentState('API_SUCCESS')
        setShowLoader(false)
        return
      }

      // SMS factor - fetch phone number from API
      setShowLoader(true)
      await axios
        .get(api.getPhoneNumber(), {
          params: {
            Id: response.user.id,
            stateToken: response.data.stateToken,
            factorId: response.factor.id,
          },
        })
        .then((res) => {
          localStorage.setItem('mobileNumber', res.data.phoneNumber)
          setMobileNumber(res.data.phoneNumber)
          setShowLoader(false)
          setCurrentState('API_SUCCESS')
        })
        .catch(() => {
          setCurrentState('API_FAIL')
          setShowLoader(false)
        })
    } else if (
      response.status === constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL_ACTIVATE
    ) {
      const number = localStorage.getItem('mobileNumber')
      setCurrentState('API_SUCCESS')
      setMobileNumber(number)
    }
  }

  /**
   * Resend OTP in case of pre and post enrollment
   * @param transaction transaction
   */
  const resendOTP = async (transaction: any) => {
    setResendAlert(false)
    setShowLoader(true)
    await axios
      .post(
        `https://${process.env.REACT_APP_OKTA_DOMAIN}/api/v1/authn/factors/${
          transaction.factor.id
        }/${
          transaction.status ===
          constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL_ACTIVATE
            ? 'lifecycle'
            : transaction.status ===
                constants.OKTA_TRANSACTION_STATUS.MFA_CHALLENGE && 'verify'
        }/resend`,
        {
          stateToken: `${transaction.data.stateToken}`,
        },
      )
      .then((res) => {
        setShowLoader(false)
        if (res?.statusText === 'OK' || res?.status === 200) {
          setTooManyAttemptsAlert(false)
          setResendAlert(true)
          setAlertVisibility(false)
          setRequestFailed(false)
          setTimeout(() => {
            setResendAlert(false)
          }, 30000)
        }
      })
      .catch((error) => {
        setShowLoader(false)
        setAlertVisibility(true)
        if (error.message === 'Request failed with status code 429') {
          setTooManyAttemptsAlert(true)
          setRequestFailed(false)
          return
        }
        setResendAlert(false)
        setTooManyAttemptsAlert(false)
        setRequestFailed(true)
      })
  }

  /**
   * Redirects user to login page with the error of 'Too many attempts'
   * In the case of user entering wrong OTP in more than 5 tries.
   */
  useEffect(() => {
    setAlertVisibility(true)
    if (serviceError === 'E0000069') {
      navigate('/login', {
        state: {
          errorCode: serviceError,
          errorDate: new Date(),
        },
      })
    } else if (serviceError === 'E0000068') {
      setClearOTP(true)
    } else if (serviceError === 'E0000011') {
      navigate('/login', { state: { errorCode: serviceError } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceError])

  const getOtpInstructionText = (identifier?: string) => {
    if (factorType === 'email') {
      return (
        <>
          Enter the code we sent to{' '}
          <strong>{identifier || 'your email'}</strong> by email. This one-time
          passcode will expire in 5 minutes.
        </>
      )
    }
    if (factorType === 'totp') {
      return (
        <>
          Enter the 6-digit code from your authenticator app. The code changes
          every 30 seconds.
        </>
      )
    }
    // SMS (default)
    return (
      <>
        Enter the code we sent to{' '}
        {identifier ? formatPhoneNumber(identifier) : ' '} by text message
        (SMS). This one-time passcode will expire in 5 minutes.
      </>
    )
  }

  const renderMobileOTPForm = (mobileNumber?: string) => {
    return (
      <>
        <ModuleHeader
          title="Two-Factor Authentication"
          className={classes.otpFormHeader}
          moduleBodyClass={classes.otpModuleBodyContainer}
        >
          <p className={classes.infoContent}>
            {getOtpInstructionText(mobileNumber)}
          </p>
          <CustomAlert
            count={count}
            severity={serviceError === 'resend_success' ? 'success' : 'error'}
            handleClick={resendOTP}
            transaction={transaction}
            isVisible={alertVisibility}
            factorType={factorType}
          >
            {serviceError}
          </CustomAlert>
        </ModuleHeader>
        <ModuleBody>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values: { verificationCode },
              handleSubmit: onSubmit,
              dirty,
              isValid,
              isSubmitting,
              setFieldValue,
            }) => {
              if (clearOTP && !executeOnce) {
                executeOnce = true
                setClearOTP(false)
                setFieldValue('verificationCode', '')
              }
              return (
                <form onSubmit={onSubmit}>
                  {resendAlert && (
                    <CustomAlert severity="error">
                      {factorType === 'email' ? (
                        <>
                          A new 6-digit code has been sent to{' '}
                          <strong>{mobileNumber}</strong>. It may take up to 5
                          minutes for your code to arrive.
                        </>
                      ) : (
                        <>
                          A new 6-digit code has been sent to{' '}
                          {mobileNumber ? formatPhoneNumber(mobileNumber) : ' '}
                          . It may take up to 5 minutes for your code to arrive.
                        </>
                      )}
                    </CustomAlert>
                  )}
                  <div className={classes.otpContainer}>
                    <Typography>
                      <strong className={classes.verificationLabel}>
                        Verification Code
                      </strong>
                    </Typography>
                    <OtpInput
                      value={verificationCode}
                      onChange={(value: string) => {
                        if (value === '' || /^[0-9]*$/.test(value)) {
                          setFieldValue('verificationCode', value)
                          executeOnce = false
                        }
                      }}
                      numInputs={6}
                      inputStyle="otp-input"
                      containerStyle="otp-input-container"
                      focusStyle="otp-input--focused"
                      renderInput={(props: object) => (
                        <input {...{ ...props, inputMode: 'numeric' }} />
                      )}
                      shouldAutoFocus
                    />
                  </div>

                  {tooManyAttemptsAlert ? (
                    <CustomAlert severity="error">
                      Too many attempts. Please try again after 30 seconds.
                    </CustomAlert>
                  ) : (
                    requestFailed && (
                      <CustomAlert severity="error">
                        Please refresh the browser and try again.
                      </CustomAlert>
                    )
                  )}

                  {/* Hide resend button for TOTP since codes are generated in the app */}
                  {factorType !== 'totp' && (
                    <Box textAlign="center">
                      <div className={classes.resendContainer}>
                        <Button
                          variant="text"
                          disableRipple
                          disabled={resendAlert || disableResend}
                          className={classes.resendInfo}
                          onClick={() => {
                            // as per the new flow in BP-9230, if user taps on resend, send him back to previous screen if
                            // he wishes to update the number, then trigger sending OTP
                            if (
                              transaction.status ===
                              constants.OKTA_TRANSACTION_STATUS
                                .MFA_ENROLL_ACTIVATE
                            ) {
                              transaction
                                .prev()
                                .then((response: any) => {
                                  navigate('/enroll', {
                                    state: {
                                      // push the back to enrollment screen
                                      mobileNumber: mobileNumber,
                                      poppedFromResend: true,
                                    },
                                  })
                                })
                                .catch((e: any) => console.log('error', e))
                            } else {
                              // post enrollment case, should resend the OTP
                              resendOTP(transaction)
                            }
                          }}
                        >
                          Didn't get the code? Resend
                        </Button>
                      </div>
                    </Box>
                  )}

                  <ButtonContainer
                    primary={
                      <CustomButton
                        type="submit"
                        disabled={!dirty || !isValid || isSubmitting}
                      >
                        Continue
                      </CustomButton>
                    }
                  />

                  {onUseDifferentMethod && (
                    <Box textAlign="center" mt={2}>
                      <Button
                        variant="text"
                        disableRipple
                        className={classes.resendInfo}
                        onClick={onUseDifferentMethod}
                      >
                        Use a different verification method
                      </Button>
                    </Box>
                  )}
                </form>
              )
            }}
          </Formik>
        </ModuleBody>
      </>
    )
  }

  return (
    <Module>
      {mobileNumber &&
        currentState === 'API_SUCCESS' &&
        renderMobileOTPForm(mobileNumber)}
      {mobileNumber &&
        currentState === 'API_FAIL' &&
        renderMobileOTPForm(mobileNumber)}
    </Module>
  )
}

export default MfaOtpForm
